const SDK_VERSION = '1.1.2'
const VISITOR_KEY = 'translate_track_visitor_id'
const SESSION_KEY = 'translate_track_session_id'
const VISITOR_COOKIE_MAX_AGE = 60 * 60 * 24 * 365
const MAX_QUEUE_SIZE = 20
const FLUSH_INTERVAL = 5000
const INTERACTION_DEDUPE_MS = 500
const INPUT_ACTIVITY_THROTTLE_MS = 1200
const DEFAULT_ENDPOINT = 'https://track.wesitrans.com/track'
const UI_TRACKABLE_CLASSES = [
  'el-button',
  'el-menu-item',
  'el-dropdown-menu__item',
  'el-tabs__item',
  'el-select-dropdown__item',
  'el-checkbox',
  'el-radio',
  'el-switch',
  'el-upload',
  'el-pagination'
]
const TRACKABLE_CLASS_KEYWORDS = [
  'btn',
  'button',
  'link',
  'tab',
  'menu',
  'option',
  'select',
  'switch',
  'checkbox',
  'radio',
  'close',
  'download',
  'submit',
  'confirm',
  'cancel',
  'delete',
  'el-icon-yuedu',
  'el-icon-xiazai',
  'el-icon-youjian',
  'el-icon-xiangqing',
  'el-icon-customerarchivesrecyclebin',
  'back',
  'next',
  'prev',
  'experience',
  'goto',
  'home-button',
  'trans-pro-main-lj-button',
  'timebutton',
  'money-info',
  'step-submit',
  'check-card',
  'select-option',
  'tabs-item',
  'headermax',
  'headermin',
  'left-yijian',
  'detail-back',
  'new-chat',
  'create-ppt',
  'change-style',
  'delete-file',
  'safe-url-link',
  'title-link',
  'buy'
]
const LAYOUT_CLASS_KEYWORDS = [
  'container',
  'wrapper',
  'content',
  'layout',
  'section',
  'main',
  'body',
  'box',
  'panel',
  'group',
  'row',
  'col',
  'list',
  'form',
  'nav'
]
const INTERACTIVE_CLASS_HINTS = [
  'btn',
  'button',
  'link',
  'item',
  'tab',
  'menu-item',
  'option',
  'card',
  'download',
  'el-icon-yuedu',
  'el-icon-xiazai',
  'el-icon-youjian',
  'el-icon-xiangqing',
  'el-icon-customerarchivesrecyclebin',
  'submit',
  'confirm',
  'cancel',
  'back',
  'next',
  'prev',
  'close',
  'home-button',
  'timebutton',
  'left-yijian',
  'detail-back',
  'safe-url-link',
  'title-link'
]
const TRACKABLE_ROLES = ['button', 'link', 'menuitem', 'tab', 'checkbox', 'radio', 'switch', 'option']
const IGNORE_CLASS_KEYWORDS = ['layui-layer-shade', 'modal-mask', 'overlay', 'backdrop']

let options = {}
let queue = []
let timer
let currentRoute
let pageEnterTime = Date.now()
let maxScrollDepth = 0
let lastInteractionSignature = ''
let lastInteractionTime = 0
let lastDownloadSignature = ''
let lastDownloadTime = 0
let lastBoundIdentity = ''
const recentApiEvents = {}
const inputActivityTimes = typeof WeakMap !== 'undefined' ? new WeakMap() : null

export function setupTrack(Vue, trackOptions) {
  safely(() => {
    options = Object.assign({ sdkVersion: SDK_VERSION, endpoint: DEFAULT_ENDPOINT }, trackOptions || {})
    ensureVisitorId()
    ensureSessionId()
  })
  ;[
    patchClickListener,
    patchNetworkTracking,
    bindDownloadTracking,
    () => bindRouter(options.router),
    bindClick,
    bindFormInteractions,
    bindLifecycle,
    () => bindErrors(Vue),
    bindPerformance
  ].forEach(binding => safely(binding))
  safely(() => {
    timer = window.setInterval(() => safely(flush), FLUSH_INTERVAL)
    const isNewSession = storageGet(window.sessionStorage, `${SESSION_KEY}_started`) !== '1'
    track('session_start', { isNewSession }, 'session_start')
    storageSet(window.sessionStorage, `${SESSION_KEY}_started`, '1')
    if (getAuthToken()) trackIdentify()
  })
}

export function track(eventName, properties = {}, eventType = 'custom_event') {
  safely(() => {
    if (!options.appId) return
    enqueue({
      eventType,
      eventName,
      page: pageInfo(currentRoute),
      properties: sanitize(properties)
    })
  })
}

export function trackElementClick(element = {}, properties = {}) {
  safely(() => {
    if (!options.appId) return
    const trackId = element.trackId || ''
    if (shouldSkipInteraction(trackId)) return
    enqueue({
      eventType: 'element_click',
      eventName: element.eventName || 'element_click',
      page: pageInfo(currentRoute),
      element: {
        trackId,
        text: element.text || '',
        type: element.type || '',
        tag: element.tag || '',
        componentName: element.componentName || '',
        businessId: businessIdHash(element.businessId),
        clickX: Number(element.clickX || 0),
        clickY: Number(element.clickY || 0)
      },
      properties: sanitize(properties)
    })
    flushInteraction()
  })
}

export function trackIdentify(user = {}) {
  safely(() => {
    bindIdentity(true)
  })
}

export function trackApiRequest(config, status, businessCode, durationMs, success) {
  safely(() => {
    const url = String(config && config.url || '')
    if (!options.appId || url.indexOf('/track/') >= 0) return
    const path = safeApiPath(url)
    const method = String(config && config.method || 'POST').toUpperCase()
    const normalizedStatus = status || 0
    const key = `${method}|${path}|${normalizedStatus}`
    const now = Date.now()
    const previous = recentApiEvents[key]
    if (previous && now - previous.time < 250) {
      if (businessCode != null && String(businessCode) !== '') previous.event.api.businessCode = String(businessCode).slice(0, 40)
      previous.event.api.success = Boolean(success)
      previous.event.api.durationMs = Math.max(previous.event.api.durationMs || 0, Number(durationMs || 0))
      return
    }
    const apiEvent = {
      eventType: 'api_request',
      eventName: 'api_request',
      page: pageInfo(currentRoute),
      api: {
        path,
        method,
        httpStatus: normalizedStatus,
        businessCode: businessCode == null ? '' : String(businessCode).slice(0, 40),
        durationMs: Math.max(0, Number(durationMs || 0)),
        success: Boolean(success)
      },
      metrics: { durationMs: Math.max(0, Number(durationMs || 0)) },
      properties: sanitize({ apiSource: config && config.__trackSource || 'manual' })
    }
    recentApiEvents[key] = { time: now, event: apiEvent }
    enqueue(apiEvent)
  })
}

export function flush(useBeacon = false, preferBeacon = useBeacon) {
  if (!options.appId || queue.length === 0) return
  const pending = queue.splice(0, queue.length)
  // A restored queue can contain 200 events; the server accepts at most 100 per request.
  for (let offset = 0; offset < pending.length; offset += MAX_QUEUE_SIZE) {
    sendBatch(pending.slice(offset, offset + MAX_QUEUE_SIZE), useBeacon, preferBeacon)
  }
}

function sendBatch(events, useBeacon, preferBeacon) {
  try {
    const payload = JSON.stringify({
      appId: options.appId,
      sdkVersion: options.sdkVersion || SDK_VERSION,
      visitorId: ensureVisitorId(),
      sessionId: ensureSessionId(),
      events
    })
    const url = `${trimEnd(options.endpoint || DEFAULT_ENDPOINT)}/events/batch`
    const requestId = uuid()
    if (preferBeacon && !getAuthToken() && navigator.sendBeacon) {
      try {
        if (navigator.sendBeacon(diagnosticUrl(url, payload, 'beacon', requestId), new Blob([payload], { type: 'application/json' }))) return
      } catch (e) {}
    }
    sendJson(url, payload, {
      keepalive: useBeacon,
      expectedAccepted: events.length,
      requestId,
      onFailure: () => restoreQueue(events)
    })
  } catch (e) {
    restoreQueue(events)
  }
}

function enqueue(event) {
  safely(() => {
    bindIdentity()
    queue.push(Object.assign({
      eventId: uuid(),
      clientTime: new Date().toISOString(),
      visitorId: ensureVisitorId(),
      sessionId: ensureSessionId(),
      screenWidth: window.screen && window.screen.width || 0,
      screenHeight: window.screen && window.screen.height || 0,
      viewportWidth: window.innerWidth || 0,
      viewportHeight: window.innerHeight || 0,
      networkType: navigator.connection && navigator.connection.effectiveType || ''
    }, event))
    if (queue.length >= MAX_QUEUE_SIZE) flush()
  })
}

function bindRouter(router) {
  if (!router || !router.afterEach) return
  router.afterEach((to, from) => {
    safely(() => {
      if (currentRoute) trackPageLeave(from || currentRoute)
      currentRoute = to
      pageEnterTime = Date.now()
      maxScrollDepth = 0
      enqueue({ eventType: 'page_view', eventName: 'page_view', page: pageInfo(to) })
    })
  })
}

function trackPageLeave(route) {
  enqueue({
    eventType: 'page_leave',
    eventName: 'page_leave',
    page: pageInfo(route),
    metrics: { durationMs: Date.now() - pageEnterTime, maxScrollDepth },
    durationMs: Date.now() - pageEnterTime
  })
}

function bindClick() {
  document.addEventListener('click', event => safely(() => recordElementInteraction(event, 'click')), true)
}

function bindDownloadTracking() {
  if (window.__translateTrackDownloadPatched || !window.HTMLAnchorElement) return
  window.__translateTrackDownloadPatched = true
  const nativeClick = window.HTMLAnchorElement.prototype.click
  window.HTMLAnchorElement.prototype.click = function () {
    recordDownloadLink(this, 'programmatic_click')
    return nativeClick.apply(this, arguments)
  }
  document.addEventListener('click', event => {
    const target = event.target
    const link = target && target.closest ? target.closest('a') : null
    if (link) recordDownloadLink(link, 'user_click')
  }, true)
}

function recordDownloadLink(link, trigger) {
  if (!link || !options.appId) return
  const downloadName = link.download || link.getAttribute('download') || ''
  const href = link.getAttribute('href') || link.href || ''
  const isDownload = Boolean(downloadName) || href.indexOf('blob:') === 0 || href.indexOf('data:') === 0 || href.toLowerCase().indexOf('download') >= 0
  if (!isDownload) return
  const signature = `${downloadName}|${href}`.slice(0, 300)
  const now = Date.now()
  if (signature === lastDownloadSignature && now - lastDownloadTime < 1000) return
  lastDownloadSignature = signature
  lastDownloadTime = now
  track('download_attempt', {
    downloadTrigger: trigger,
    downloadType: href.indexOf('blob:') === 0 ? 'blob' : href.indexOf('data:') === 0 ? 'data' : 'url',
    fileExtension: fileExtension(downloadName),
    fileNameHash: downloadName ? hash(downloadName).slice(0, 16) : '',
    hasDownloadName: Boolean(downloadName)
  }, 'download_attempt')
}

function recordElementInteraction(event, trigger) {
  if (event.button && event.button !== 0) return
  const match = closestTrackable(event.target)
  if (!match) return
  const element = match.element
  const dataset = element.dataset || {}
  const trackId = dataset.trackId || buildElementId(element)
  if (shouldSkipInteraction(trackId)) return
  enqueue({
    eventType: 'element_click',
    eventName: dataset.trackName || 'element_click',
    page: pageInfo(currentRoute),
    element: {
      trackId,
      text: safeText(element),
      type: element.getAttribute('role') || element.getAttribute('type') || '',
      tag: element.tagName.toLowerCase(),
      componentName: dataset.trackComponent || '',
      businessId: businessIdHash(dataset.trackBusinessId),
      clickX: Math.round(event.clientX || 0),
      clickY: Math.round(event.clientY || 0)
    },
    properties: Object.assign({
      trackSource: match.source,
      elementClasses: elementClass(element),
      interactionTrigger: trigger
    }, safeElementMetadata(element), {
      dataAttributeKeys: safeDatasetKeys(dataset)
    }, parseExtra(dataset.trackExtra))
  })
  flushInteraction()
}

function bindFormInteractions() {
  const bindings = [
    ['focusin', 'field_focus'],
    ['focusout', 'field_blur'],
    ['input', 'field_input'],
    ['change', 'field_change'],
    ['invalid', 'field_invalid'],
    ['submit', 'form_submit'],
    ['reset', 'form_reset'],
    ['copy', 'clipboard_copy'],
    ['cut', 'clipboard_cut'],
    ['paste', 'clipboard_paste'],
    ['drop', 'content_drop']
  ]
  bindings.forEach(([domEvent, eventName]) => {
    document.addEventListener(domEvent, event => safely(() => recordFormInteraction(event, eventName)), true)
  })
  document.addEventListener('keydown', event => {
    safely(() => {
      if (['Enter', 'Escape', ' '].indexOf(event.key) < 0) return
      recordFormInteraction(event, 'keyboard_action', { key: event.key === ' ' ? 'Space' : event.key })
    })
  }, true)
}

function recordFormInteraction(event, eventName, extra = {}) {
  if (!options.appId) return
  let element = event.target
  if (!element || !element.tagName) return
  if (eventName.indexOf('form_') === 0 && element.tagName.toLowerCase() !== 'form') {
    element = element.closest && element.closest('form')
  }
  if (!element || !isFormTrackable(element, eventName)) return
  if (element.dataset && element.dataset.trackIgnore != null) return
  if (eventName === 'field_input' && shouldThrottleInput(element)) return
  const dataset = element.dataset || {}
  const trackId = dataset.trackId || buildElementId(element)
  enqueue({
    eventType: eventName,
    eventName: dataset.trackName || eventName,
    page: pageInfo(currentRoute),
    element: {
      trackId,
      text: safeFormText(element),
      type: element.getAttribute('role') || element.getAttribute('type') || '',
      tag: element.tagName.toLowerCase(),
      componentName: dataset.trackComponent || '',
      businessId: businessIdHash(dataset.trackBusinessId),
      clickX: 0,
      clickY: 0
    },
    properties: Object.assign({
      interactionTrigger: event.type,
      elementClasses: elementClass(element),
      dataAttributeKeys: safeDatasetKeys(dataset)
    }, safeElementMetadata(element, event), sanitize(extra), parseExtra(dataset.trackExtra))
  })
}

function isFormTrackable(element, eventName) {
  const tag = element.tagName.toLowerCase()
  if (eventName.indexOf('form_') === 0) return tag === 'form'
  if (eventName.indexOf('clipboard_') === 0) return tag === 'input' || tag === 'textarea' || element.isContentEditable
  if (eventName === 'content_drop') return Boolean(event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length) || tag === 'input' || element.isContentEditable
  if (eventName === 'keyboard_action') return tag === 'input' || tag === 'textarea' || tag === 'select' || tag === 'button' || tag === 'a' || element.isContentEditable
  return tag === 'input' || tag === 'textarea' || tag === 'select' || element.isContentEditable
}

function shouldThrottleInput(element) {
  if (!inputActivityTimes) return false
  const now = Date.now()
  const last = inputActivityTimes.get(element) || 0
  inputActivityTimes.set(element, now)
  return now - last < INPUT_ACTIVITY_THROTTLE_MS
}

function flushInteraction() {
  if (options.flushOnClick === false) return
  flush(true, false)
}

function shouldSkipInteraction(trackId) {
  const signature = `${pageInfo(currentRoute).routePath}|${trackId || ''}`
  const now = Date.now()
  if (signature === lastInteractionSignature && now - lastInteractionTime < INTERACTION_DEDUPE_MS) return true
  lastInteractionSignature = signature
  lastInteractionTime = now
  return false
}

function bindLifecycle() {
  window.addEventListener('scroll', () => {
    safely(() => {
      const doc = document.documentElement
      const max = Math.max(1, doc.scrollHeight - window.innerHeight)
      maxScrollDepth = Math.max(maxScrollDepth, Math.min(100, Math.round(((window.scrollY || doc.scrollTop || 0) / max) * 100)))
    })
  }, { passive: true })
  document.addEventListener('visibilitychange', () => {
    safely(() => {
      if (document.visibilityState === 'hidden') {
        trackPageLeave(currentRoute)
        flush(true)
      }
    })
  })
  window.addEventListener('beforeunload', () => {
    safely(() => {
      track('session_end', { durationMs: Date.now() - pageEnterTime }, 'session_end')
      flush(true)
      if (timer) window.clearInterval(timer)
    })
  })
}

function bindErrors(Vue) {
  window.addEventListener('error', event => {
    safely(() => {
      const target = event.target
      const isResource = target && target !== window && Boolean(target.src || target.href)
      trackError(isResource ? 'resource_error' : 'js_error', event.message || 'resource load error', event.error && event.error.stack || '')
    })
  }, true)
  window.addEventListener('unhandledrejection', event => {
    safely(() => {
      const reason = event.reason || {}
      trackError('promise_rejection', reason.message || String(reason), reason.stack || '')
    })
  })
  if (Vue && Vue.config) {
    const oldHandler = Vue.config.errorHandler
    Vue.config.errorHandler = (err, vm, info) => {
      trackError('vue_error', err && err.message || String(err), err && err.stack || info)
      if (oldHandler) oldHandler(err, vm, info)
    }
  }
}

function bindPerformance() {
  window.addEventListener('load', () => {
    window.setTimeout(() => {
      safely(() => {
        const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0]
        if (!nav) return
        enqueue({
          eventType: 'performance',
          eventName: 'navigation_timing',
          page: pageInfo(currentRoute),
          performance: {
            ttfbMs: Math.round(nav.responseStart),
            domReadyMs: Math.round(nav.domContentLoadedEventEnd),
            loadMs: Math.round(nav.loadEventEnd)
          }
        })
      })
    }, 0)
  })
}

function trackError(type, message, stack) {
  enqueue({
    eventType: 'frontend_error',
    eventName: type,
    page: pageInfo(currentRoute),
    error: { type, message, stack, fingerprint: hash(`${type}|${message}|${stack}`) }
  })
  flush()
}

function pageInfo(route = {}) {
  const routePath = route.path || window.location.pathname
  return {
    routePath,
    routeName: route.name ? String(route.name) : '',
    title: route.meta && route.meta.title || document.title || '',
    urlHost: window.location.host,
    urlPath: routePath,
    urlQueryHash: hash(window.location.search || ''),
    searchKeyNames: searchKeyNames(),
    referrerHost: safeUrlPart(document.referrer, 'host'),
    referrerPath: safeUrlPart(document.referrer, 'pathname')
  }
}

function closestTrackable(target) {
  let node = target
  let fallback = null
  let strong = null
  while (node && node !== document.body) {
    const inputLabel = checkableInputLabel(node)
    if (inputLabel) {
      node = inputLabel
      continue
    }
    if (node.dataset && node.dataset.trackIgnore != null) return null
    if (hasIgnoredClass(node)) return null
    if (node.dataset && (node.dataset.trackId || node.dataset.trackText)) {
      return { element: node, source: 'data_track' }
    }
    const source = strongTrackSource(node)
    if (!strong && source) strong = { element: node, source }
    const fallbackSource = fallbackTrackSource(node)
    if (!fallback && fallbackSource) fallback = { element: node, source: fallbackSource }
    node = node.parentElement
  }
  return strong || fallback
}

function checkableInputLabel(node) {
  if (!node || !node.tagName) return null
  if (node.tagName.toLowerCase() !== 'input') return false
  if (!/^(checkbox|radio)$/i.test(node.getAttribute('type') || '')) return false
  return node.closest && node.closest('label')
}

function strongTrackSource(node) {
  if (!node || !node.tagName) return ''
  const tag = node.tagName.toLowerCase()
  const role = node.getAttribute && String(node.getAttribute('role') || '').toLowerCase()
  if (node.dataset && (node.dataset.trackId || node.dataset.trackText)) return 'data_track'
  if (tag === 'button' || tag === 'a') return `native_${tag}`
  if (tag === 'input' && /^(button|submit|reset|checkbox|radio)$/i.test(node.getAttribute('type') || '')) return 'native_input'
  if (TRACKABLE_ROLES.indexOf(role) >= 0) return `role_${role}`
  if (hasAnyClass(node, UI_TRACKABLE_CLASSES)) return 'ui_component'
  if (node.dataset && node.dataset.trackClickBound) return 'event_listener'
  return ''
}

function patchClickListener() {
  if (!window.EventTarget || window.__translateTrackClickListenerPatched) return
  const rawAddEventListener = window.EventTarget.prototype.addEventListener
  Object.defineProperty(window, '__translateTrackClickListenerPatched', {
    value: true,
    configurable: false
  })
  window.EventTarget.prototype.addEventListener = function (type, listener, options) {
    if (type === 'click' && this && this.nodeType === 1 && this.setAttribute) {
      this.setAttribute('data-track-click-bound', '1')
    }
    return rawAddEventListener.call(this, type, listener, options)
  }
}

function patchNetworkTracking() {
  if (window.__translateTrackNetworkPatched) return
  Object.defineProperty(window, '__translateTrackNetworkPatched', { value: true, configurable: false })
  if (window.XMLHttpRequest && window.XMLHttpRequest.prototype) {
    const rawOpen = window.XMLHttpRequest.prototype.open
    const rawSend = window.XMLHttpRequest.prototype.send
    window.XMLHttpRequest.prototype.open = function (method, url) {
      safely(() => {
        this.__translateTrackRequest = {
          method: String(method || 'GET').toUpperCase(),
          url: String(url || ''),
          start: 0
        }
      })
      return rawOpen.apply(this, arguments)
    }
    window.XMLHttpRequest.prototype.send = function () {
      const xhr = this
      safely(() => {
        const request = xhr.__translateTrackRequest || { method: 'GET', url: '', start: 0 }
        request.start = Date.now()
        xhr.__translateTrackRequest = request
        xhr.addEventListener('loadend', () => safely(() => {
          const businessCode = safeBusinessCode(xhr)
          trackApiRequest({
            url: request.url,
            method: request.method,
            __trackSource: 'xhr'
          }, xhr.status || 0, businessCode, Date.now() - request.start, xhr.status >= 200 && xhr.status < 400)
        }), { once: true })
      })
      return rawSend.apply(this, arguments)
    }
  }
  if (typeof window.fetch === 'function') {
    const rawFetch = window.fetch
    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : input && input.url || ''
      const method = init && init.method || input && input.method || 'GET'
      const start = Date.now()
      return rawFetch.apply(this, arguments).then(response => {
        safely(() => trackApiRequest({ url, method, __trackSource: 'fetch' }, response.status, '', Date.now() - start, response.ok))
        return response
      }, error => {
        safely(() => trackApiRequest({ url, method, __trackSource: 'fetch' }, 0, error && error.code || '', Date.now() - start, false))
        throw error
      })
    }
  }
}

function safeBusinessCode(xhr) {
  try {
    const contentType = String(xhr.getResponseHeader('content-type') || '').toLowerCase()
    if (contentType.indexOf('json') < 0 || typeof xhr.responseText !== 'string' || xhr.responseText.length > 1024 * 1024) return ''
    const parsed = JSON.parse(xhr.responseText || '{}')
    return parsed && parsed.code != null ? String(parsed.code).slice(0, 40) : ''
  } catch (e) {
    return ''
  }
}

function fallbackTrackSource(node) {
  if (!node || !node.tagName || !node.getAttribute) return ''
  const tag = node.tagName.toLowerCase()
  if (tag === 'html' || tag === 'body') return ''
  const tabindex = node.getAttribute('tabindex')
  if (tabindex != null && tabindex !== '-1') return 'tabindex'
  const classes = elementClass(node).toLowerCase()
  if (isLayoutOnlyClass(classes)) return ''
  if (TRACKABLE_CLASS_KEYWORDS.some(keyword => classes.indexOf(keyword) >= 0)) return 'class_keyword'
  if (window.getComputedStyle && window.getComputedStyle(node).cursor === 'pointer') return 'cursor_pointer'
  return ''
}

function hasIgnoredClass(node) {
  const classes = elementClass(node).toLowerCase()
  return IGNORE_CLASS_KEYWORDS.some(keyword => classes.indexOf(keyword) >= 0)
}

function isLayoutOnlyClass(classes) {
  if (!classes) return false
  if (INTERACTIVE_CLASS_HINTS.some(keyword => classes.indexOf(keyword) >= 0)) return false
  return LAYOUT_CLASS_KEYWORDS.some(keyword => classes.indexOf(keyword) >= 0)
}

function ensureVisitorId() {
  let id = sharedCookieGet(VISITOR_KEY) || storageGet(window.localStorage, VISITOR_KEY)
  if (!id) {
    id = uuid()
  }
  storageSet(window.localStorage, VISITOR_KEY, id)
  sharedCookieSet(VISITOR_KEY, id)
  return id
}

function ensureSessionId() {
  let id = storageGet(window.sessionStorage, SESSION_KEY)
  if (!id) {
    id = uuid()
    storageSet(window.sessionStorage, SESSION_KEY, id)
  }
  return id
}

function sanitize(input = {}) {
  const output = {}
  Object.keys(input).forEach(key => {
    if (isSensitiveKey(key)) return
    const value = input[key]
    if (value == null) return
    output[key] = Array.isArray(value) ? value.join(',') : (typeof value === 'object' ? '[object]' : String(value).slice(0, 200))
  })
  return output
}

function post(path, payload) {
  safely(() => {
    sendJson(`${trimEnd(options.endpoint || DEFAULT_ENDPOINT)}${path}`, JSON.stringify(payload), { keepalive: true })
  })
}

function sendJson(url, payload, requestOptions = {}) {
  let failed = false
  const onFailure = () => {
    if (failed) return
    failed = true
    if (requestOptions.onFailure) requestOptions.onFailure()
  }
  const accepted = body => body && Number(body.code) === 200 &&
    (requestOptions.expectedAccepted == null ||
      (body.data && Number(body.data.accepted) === requestOptions.expectedAccepted))
  try {
    const authToken = getAuthToken()
    const requestId = requestOptions.requestId || uuid()
    if (typeof fetch === 'function') {
      fetch(diagnosticUrl(url, payload, requestOptions.keepalive ? 'fetch-keepalive' : 'fetch', requestId), {
        method: 'POST',
        headers: authHeaders(authToken),
        body: payload,
        keepalive: Boolean(requestOptions.keepalive),
        credentials: 'include'
      }).then(response => {
        if (!response.ok) {
          onFailure()
          return
        }
        // Older servers return HTTP 200 even when JSON parsing or ingestion failed.
        return response.json().then(body => {
          if (!accepted(body)) onFailure()
        })
      }).catch(onFailure)
      return
    }
    if (typeof XMLHttpRequest === 'undefined') {
      onFailure()
      return
    }
    const xhr = new XMLHttpRequest()
    xhr.open('POST', diagnosticUrl(url, payload, 'xhr', requestId), true)
    xhr.withCredentials = true
    xhr.timeout = 5000
    xhr.setRequestHeader('Content-Type', 'application/json')
    if (authToken) xhr.setRequestHeader('Authorization', authToken)
    xhr.onload = () => {
      try {
        if (xhr.status < 200 || xhr.status >= 300 || !accepted(JSON.parse(xhr.responseText))) onFailure()
      } catch (e) {
        onFailure()
      }
    }
    xhr.onerror = xhr.ontimeout = onFailure
    xhr.send(payload)
  } catch (e) {
    // Synchronous fetch/XHR failures must restore events too.
    onFailure()
  }
}

// URL metadata survives a damaged body and supports beacon, which cannot set custom headers.
function diagnosticUrl(url, payload, transport, requestId) {
  try {
    const bytes = typeof Blob === 'function' ? new Blob([payload]).size :
      encodeURIComponent(payload).replace(/%[A-F\d]{2}/gi, 'x').length
    const metadata = { id: requestId, method: 'POST', transport, bytes, hash: hash(payload) }
    const query = Object.keys(metadata).map(key => `__track_${key}=${encodeURIComponent(metadata[key])}`).join('&')
    return `${url}${url.indexOf('?') < 0 ? '?' : '&'}${query}`
  } catch (e) {
    return url
  }
}

function safeText(element) {
  if (!element || !element.getAttribute) return ''
  const tag = element.tagName && element.tagName.toLowerCase()
  if (tag === 'input' || tag === 'textarea' || element.isContentEditable) return safeFormText(element)
  const image = element.querySelector && element.querySelector('img[alt]')
  const text = element.dataset.trackText ||
    element.getAttribute('aria-label') ||
    element.getAttribute('title') ||
    element.getAttribute('alt') ||
    (image && image.getAttribute('alt')) ||
    element.innerText ||
    element.textContent ||
    nearbyElementText(element) ||
    fallbackElementText(element) ||
    ''
  return text.replace(/\s+/g, ' ').trim().slice(0, 80)
}

function safeFormText(element) {
  if (!element || !element.getAttribute) return ''
  const dataset = element.dataset || {}
  const text = dataset.trackText ||
    element.getAttribute('aria-label') ||
    element.getAttribute('title') ||
    element.getAttribute('placeholder') ||
    element.getAttribute('name') ||
    element.getAttribute('type') ||
    (element.tagName && element.tagName.toLowerCase()) ||
    ''
  return String(text).replace(/\s+/g, ' ').trim().slice(0, 80)
}

function safeElementMetadata(element, event) {
  if (!element || !element.getAttribute) return {}
  const tag = (element.tagName || '').toLowerCase()
  const href = tag === 'a' ? element.getAttribute('href') || '' : ''
  const action = tag === 'form' ? element.getAttribute('action') || '' : ''
  const files = event && event.dataTransfer && event.dataTransfer.files
    ? Array.from(event.dataTransfer.files)
    : (element.files ? Array.from(element.files) : [])
  const metadata = {
    domId: element.id || '',
    fieldName: element.getAttribute('name') || '',
    role: element.getAttribute('role') || '',
    controlType: element.getAttribute('type') || '',
    ariaLabel: element.getAttribute('aria-label') || '',
    title: element.getAttribute('title') || '',
    target: element.getAttribute('target') || '',
    hrefHost: safeUrlPart(href, 'host'),
    hrefPath: safeUrlPart(href, 'pathname'),
    formActionPath: safeUrlPart(action, 'pathname'),
    formMethod: (element.getAttribute('method') || '').toUpperCase(),
    disabled: Boolean(element.disabled),
    readOnly: Boolean(element.readOnly),
    required: Boolean(element.required),
    multiple: Boolean(element.multiple),
    checked: Boolean(element.checked),
    selectedIndex: tag === 'select' ? element.selectedIndex : '',
    optionCount: tag === 'select' && element.options ? element.options.length : '',
    accept: element.getAttribute('accept') || '',
    autocomplete: element.getAttribute('autocomplete') || '',
    maxLength: Number(element.maxLength) >= 0 ? element.maxLength : '',
    fileCount: files.length,
    fileExtensions: files.map(file => fileExtension(file.name)).filter(Boolean).join(','),
    fileSizeBuckets: files.map(file => fileSizeBucket(file.size)).join(','),
    isContentEditable: Boolean(element.isContentEditable)
  }
  return sanitize(metadata)
}

function safeDatasetKeys(dataset) {
  if (!dataset) return ''
  return Object.keys(dataset)
    .filter(key => !isSensitiveKey(key))
    .sort()
    .slice(0, 40)
    .join(',')
}

function nearbyElementText(element) {
  const parent = element && element.parentElement
  if (!parent) return ''
  const classes = elementClass(parent).toLowerCase()
  if (classes.indexOf('down-type') >= 0 || classes.indexOf('down-type2') >= 0 || classes.indexOf('menu-item') >= 0) {
    return parent.innerText || parent.textContent || ''
  }
  return ''
}

function fallbackElementText(element) {
  const classes = elementClass(element).toLowerCase()
  const src = element.getAttribute && (element.getAttribute('src') || element.getAttribute('href') || '')
  const value = `${classes} ${src}`.toLowerCase()
  if (!value) return ''
  if (value.indexOf('qq') >= 0) return 'QQ'
  if (value.indexOf('wx') >= 0 || value.indexOf('wechat') >= 0) return '微信'
  if (value.indexOf('left-yijian') >= 0) return '意见反馈'
  if (value.indexOf('aiwenda') >= 0 || value.indexOf('aitalk') >= 0) return 'AI问答'
  if (value.indexOf('ai.png') >= 0) return 'AI助手'
  if (value.indexOf('wenbengaoliang') >= 0 || value.indexOf('high-light') >= 0) return '高亮'
  if (value.indexOf('biji') >= 0 || value.indexOf('note') >= 0) return '笔记'
  if (value.indexOf('trans-convert') >= 0 || value.indexOf('transform') >= 0) return '切换语种'
  if (value.indexOf('filter') >= 0) return '筛选'
  if (value.indexOf('user') >= 0 || value.indexOf('login.png') >= 0) return '个人中心'
  if (value.indexOf('shousuo') >= 0 || value.indexOf('collapse-menu') >= 0) return '展开收起菜单'
  if (value.indexOf('pack-up') >= 0 || value.indexOf('zhankai') >= 0) return '收起'
  if (value.indexOf('show-origin') >= 0) return '显示原文'
  if (value.indexOf('show-translate') >= 0) return '显示译文'
  if (value.indexOf('show-all') >= 0) return '原文译文并排'
  if (value.indexOf('logo') >= 0) return 'logo'
  if (value.indexOf('help') >= 0) return '帮助'
  if (value.indexOf('detail') >= 0 || value.indexOf('xiangqing') >= 0) return '查看详情'
  if (value.indexOf('read') >= 0 || value.indexOf('yuedu') >= 0) return '阅读文档'
  if (value.indexOf('mail') >= 0 || value.indexOf('youjian') >= 0) return '发送邮件'
  if (value.indexOf('close') >= 0 || value.indexOf('cancel') >= 0) return '关闭'
  if (value.indexOf('clear') >= 0) return '清空'
  if (value.indexOf('delete') >= 0 || value.indexOf('trash') >= 0 || value.indexOf('his_del') >= 0 || value.indexOf('shanchu') >= 0 || value.indexOf('guanbi2') >= 0 || value.indexOf('lajitong') >= 0 || value.indexOf('customerarchivesrecyclebin') >= 0) return '删除'
  if (value.indexOf('download') >= 0 || value.indexOf('xiazai') >= 0) return '下载'
  if (value.indexOf('back') >= 0 || value.indexOf('return') >= 0) return '返回'
  if (value.indexOf('fullscreen') >= 0 || value.indexOf('screenfull') >= 0) return '全屏'
  if (value.indexOf('data-checkbox') >= 0 || value.indexOf('el-icon-check') >= 0) return '选择'
  if (value.indexOf('copy') >= 0) return '复制'
  if (value.indexOf('refresh') >= 0) return '刷新'
  if (value.indexOf('upload') >= 0 || value.indexOf('paperclip') >= 0) return '上传文件'
  if (value.indexOf('send-message') >= 0 || value.indexOf('send.png') >= 0) return '发送'
  if (value.indexOf('preview-arrow') >= 0 && value.indexOf('prev') >= 0) return '上一张'
  if (value.indexOf('preview-arrow') >= 0 && value.indexOf('next') >= 0) return '下一张'
  const fileName = src.split('/').pop() || ''
  if (/^docx?\\./i.test(fileName)) return fileName.toLowerCase().indexOf('docx') === 0 ? 'DOCX格式' : 'DOC格式'
  if (/^pdf\\./i.test(fileName)) return 'PDF格式'
  if (/^txt\\./i.test(fileName)) return 'TXT格式'
  if (/^open\\./i.test(fileName)) return '展开收起'
  if (/^other\\./i.test(fileName)) return '切换视图'
  return fileName.replace(/\.[a-z0-9]+$/i, '').replace(/[-_]+/g, ' ').trim()
}

function buildElementId(element) {
  const signature = [
    safeText(element),
    elementClass(element),
    domPosition(element)
  ].join('|')
  return `${pageInfo(currentRoute).routePath}:${element.tagName.toLowerCase()}:${hash(signature).slice(0, 10)}`
}

function parseExtra(value) {
  if (!value) return {}
  try { return sanitize(JSON.parse(value)) } catch (e) { return {} }
}

function safeApiPath(value) {
  try { return new URL(value, window.location.origin).pathname } catch (e) { return value || '' }
}

function searchKeyNames() {
  try {
    const keys = []
    new URLSearchParams(window.location.search || '').forEach((value, key) => {
      if (!isSensitiveKey(key) && keys.indexOf(key) < 0) keys.push(key)
    })
    return keys.sort().slice(0, 30).join(',')
  } catch (e) {
    return ''
  }
}

function fileExtension(value) {
  const match = String(value || '').toLowerCase().match(/\.([a-z0-9]{1,12})(?:$|\?)/)
  return match ? match[1] : ''
}

function fileSizeBucket(size) {
  const value = Number(size || 0)
  if (value <= 0) return 'unknown'
  if (value < 1024 * 1024) return 'lt_1mb'
  if (value < 10 * 1024 * 1024) return '1_10mb'
  if (value < 50 * 1024 * 1024) return '10_50mb'
  return 'gte_50mb'
}

function safeUrlPart(value, key) {
  try { return value ? new URL(value)[key] : '' } catch (e) { return '' }
}

function elementClass(element) {
  if (!element || !element.className) return ''
  if (typeof element.className === 'string') return element.className.replace(/\s+/g, ' ').trim().slice(0, 200)
  if (element.className.baseVal) return element.className.baseVal.replace(/\s+/g, ' ').trim().slice(0, 200)
  return ''
}

function hasAnyClass(element, classNames) {
  return classNames.some(className => element.classList && element.classList.contains(className))
}

function domPosition(element) {
  const parts = []
  let node = element
  let depth = 0
  while (node && node !== document.body && depth < 4) {
    let index = 1
    let prev = node.previousElementSibling
    while (prev) {
      if (prev.tagName === node.tagName) index += 1
      prev = prev.previousElementSibling
    }
    parts.unshift(`${node.tagName.toLowerCase()}:${index}`)
    node = node.parentElement
    depth += 1
  }
  return parts.join('>')
}

function uuid() {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID()
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function hash(value) {
  let h = 5381
  for (let i = 0; i < value.length; i++) h = ((h << 5) + h) + value.charCodeAt(i)
  return (h >>> 0).toString(16)
}

function trimEnd(value) {
  return value.endsWith('/') ? value.slice(0, -1) : value
}

function businessIdHash(value) {
  return value == null || String(value).trim() === '' ? '' : hash(String(value)).slice(0, 16)
}

function getAuthToken() {
  return String((safely(() => options.getToken ? options.getToken() : '') || storageGet(window.localStorage, 'token') || '')).trim()
}

function authHeaders(authToken) {
  const headers = { 'Content-Type': 'application/json' }
  if (authToken) headers.Authorization = authToken
  return headers
}

function bindIdentity(force = false) {
  const authToken = getAuthToken()
  if (!authToken) return
  const signature = `${hash(authToken)}|${ensureSessionId()}`
  if (!force && signature === lastBoundIdentity) return
  lastBoundIdentity = signature
  post('/identity/bind', {
    appId: options.appId,
    visitorId: ensureVisitorId(),
    sessionId: ensureSessionId(),
    bindTime: new Date().toISOString()
  })
  if (force) track('identity_bind', { hasAuthToken: true }, 'identity_bind')
}

function isSensitiveKey(key) {
  return /password|passwd|token|secret|mobile|phone|email|idcard|identity(?!Types)|captcha|validate|verification|authorization|cookie|raw|payload|request|response|body|content|value|realName|orgName|fileName|orderText/i.test(String(key || ''))
}

function storageGet(storage, key) {
  try { return storage && storage.getItem(key) || '' } catch (e) { return '' }
}

function storageSet(storage, key, value) {
  try { if (storage) storage.setItem(key, value) } catch (e) {}
}

function sharedCookieGet(name) {
  try {
    if (!/(^|\.)wesitrans\.com$/i.test(window.location.hostname)) return ''
    const prefix = `${encodeURIComponent(name)}=`
    const part = document.cookie.split(';').map(item => item.trim()).find(item => item.indexOf(prefix) === 0)
    return part ? decodeURIComponent(part.slice(prefix.length)) : ''
  } catch (e) {
    return ''
  }
}

function sharedCookieSet(name, value) {
  try {
    if (!/(^|\.)wesitrans\.com$/i.test(window.location.hostname)) return
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Max-Age=${VISITOR_COOKIE_MAX_AGE}; Path=/; Domain=.wesitrans.com; SameSite=Lax; Secure`
  } catch (e) {}
}

function restoreQueue(events) {
  if (!events || !events.length) return
  queue = events.concat(queue).slice(0, 200)
}

function safely(fn) {
  try { return fn() } catch (e) { return undefined }
}

function first(...values) {
  for (const value of values) {
    if (value != null && String(value).trim()) return String(value)
  }
  return ''
}
